import React, { useContext, useMemo, useState } from 'react';
import { Box, Divider } from '@mui/material';
import { OATypography } from '../../../../components/widgets';
import OAButton from '../../../../components/widgets/OAButton';
import { ReactComponent as SourceDestIcon } from '../../../../assets/icons/source-DestArrow.svg';
import { ReactComponent as LayoverIcon } from '../../../../assets/icons/layOverIcon.svg';
import OAModalComponent from '../../../../components/OAModalComponent';
import { getPNRColumns } from './PNRColumns';
import FlightContext from '../../context/FlightContext';
import { TripDetail } from './TripDetail';
import { theme } from '../../../../config/theme';
import { getFormattedDate } from '../../../../helper';
import OATableComponent from '../../../../components/OATableComponent';

const TripType = ({
  onward,
  onwardDeparture,
  onwardArrival,
  checkIfSameDay,
  data,
  header
}: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<any>(null);
  const [segmentPNRs, setSegmentPNRs] = useState<Record<string, string>>({}); // Store PNRs for each segment
  const [ticketNumbers, setTicketNumbers] = useState<Record<string, string>>({}); // Store ticket numbers for each traveller
  const { updatePNR } = useContext(FlightContext);

  const togglePNRModal = (segment?: any) => {
    if (segment) {
      setSelectedSegment(segment);
    } else {
      setSelectedSegment(null);
    }
    setIsOpen(prev => !prev);
  };

  const submitHandler = async () => {
    setIsLoading(true);
    
    // Create draft by mapping travellers with their ticket information for the selected segment only
    let draft: any[] = [];
    
    if (selectedSegment) {
      const segmentId = selectedSegment?.segmentId;
      const segmentKey = `segment_${segmentId}`;
      
      data?.travellers?.forEach((traveller: any) => {
        const travellerKey = `${traveller.id}_${segmentId}`;
        
        // Get existing booking info for this segment
        const existingBookingInfo = selectedSegment?.bookingInfos?.[selectedSegment?.bookingInfos?.length - 1];
        
        // Get updated values - prioritize segment-specific values
        const updatedTicketNumber = ticketNumbers[travellerKey] || ticketNumbers[traveller.id];
        const updatedPNR = segmentPNRs[segmentKey] || segmentPNRs[segmentId];
        
        // Use existing values if no updates provided
        const flightTicketInfo = onward?.ticketNumberInfo?.find(
          (info: any) => info?.bookingInfoId === existingBookingInfo?.bookingInfoId
        );
        const finalTicketNumber = updatedTicketNumber ?? existingBookingInfo?.ticketNumber ?? flightTicketInfo?.ticketNumber ?? null;
        const finalPNR = updatedPNR ?? existingBookingInfo?.pnrNumber ?? selectedSegment?.pnrNumber ?? null;
        
        const item = {
          travellerId: traveller?.id,
          ticketNumber: finalTicketNumber,
          pnrNumber: finalPNR
        };
        
        // Only include items that have either a ticket number or PNR
        if (item.ticketNumber || item.pnrNumber) {
          draft.push(item);
        }
      });
    }

    let queryDraft = {
      id: data?.id,
      tripId: onward?.id,
      segmentId: selectedSegment?.segmentId?.toString()
    };

    updatePNR(draft, queryDraft, (res: any) => {
      setIsLoading(false);
      togglePNRModal();
    });
  };

  return (
    <div className='order-view--activity card pb-6'>
      <Box display='flex' justifyContent='space-between' alignItems='center' padding='16px'>
        <h3 className='card-title fw-bolder text-dark ps-4'>{header}</h3>
      </Box>
      <Divider />
      <TripDetail
        onward={onward}
        onwardDeparture={onwardDeparture}
        checkIfSameDay={checkIfSameDay}
        data={data}
        onPNRClick={(segment) => togglePNRModal(segment)}
        bookingStatus={data?.bookingStatus}
      />

      {onward?.segments?.length > 1 && (
        <>
          {onward?.segments?.map((obj: any, index: number) => {
            if (index >= 1) {
              return (
                <Box key={index}>
                  <Box
                    marginLeft='16px'
                    marginRight='16px'
                    marginTop='24px'
                    gap='8px'
                    padding='4px 12px 4px 12px'
                    display='flex'
                    height='24px'
                    borderRadius='4px'
                    alignItems='center'
                    justifyContent='center'
                    sx={{ backgroundColor: theme.palette.primary.light }}>
                    <LayoverIcon />
                    <OATypography variant='body1' sx={{ fontSize: '12px', lineHeight: '16px' }}>
                      {`Layover ${
                        onward?.layovers[index - 1]?.typeDisplay
                          ? '•' + onward?.layovers[index - 1]?.typeDisplay
                          : ''
                      } • ${onward?.layovers[index - 1]?.duration?.display} at ${
                        onward?.layovers[index - 1]?.at
                      }`}
                    </OATypography>
                  </Box>
                  <TripDetail
                    onward={onward}
                    onwardDeparture={obj}
                    checkIfSameDay={checkIfSameDay}
                    data={data}
                    onPNRClick={(segment) => togglePNRModal(segment)}
                    bookingStatus={data?.bookingStatus}
                  />
                </Box>
              );
            }
          })}
        </>
      )}
      <OAModalComponent
        size='lg' // NOTE: set modal size
        keyboard={false} // NOTE: disable keyboard event
        backdrop='static' // NOTE: disable backdrop click
        centered // NOTE: vertically center modal
        show={isOpen}
        onHide={togglePNRModal}
        header={{
          title: (
            <>
              <Box display='flex' alignItems='center' marginLeft='2px'>
                <OATypography variant='h4'>{`${
                  selectedSegment?.pnrNumber ? 'Edit' : 'Add'
                } PNR - Segment ${selectedSegment?.segmentId}`}</OATypography>
              </Box>
            </>
          ),
          closeButton: true // NOTE: show close button on header
        }}
        body={{
          content: (
            <>
              <Box>
                <TripDetailInModal
                  selectedSegment={selectedSegment}
                  segmentPNRs={segmentPNRs}
                  setSegmentPNRs={setSegmentPNRs}
                  ticketNumbers={ticketNumbers}
                  setTicketNumbers={setTicketNumbers}
                  data={data}
                />
              </Box>
              <Box
                display='flex'
                justifyContent='flex-end'
                alignItems='center'
                gap='16px'
                marginTop='16px'>
                <OAButton label='Cancel' color='secondary' onClick={() => setIsOpen(false)} />
                <OAButton label='Submit' onClick={submitHandler} isLoading={isLoading} />
              </Box>
            </>
          )
        }}
      />
    </div>
  );
};

const TripDetailInModal = ({ 
  selectedSegment, 
  segmentPNRs, 
  setSegmentPNRs, 
  ticketNumbers, 
  setTicketNumbers, 
  data 
}: any) => {
  if (!selectedSegment) return null;

  const segmentId = selectedSegment?.segmentId;
  const segmentKey = `segment_${segmentId}`;
  
  const tableColumns = useMemo(() => {
    return getPNRColumns(ticketNumbers, setTicketNumbers, segmentId);
  }, [ticketNumbers, segmentId]);

  const tableData = useMemo(
    () =>
      data?.travellers?.map((traveller: any, i: number) => {
        const travellerKey = `${traveller.id}_${segmentId}`;
        const existingBookingInfo = selectedSegment?.bookingInfos?.[selectedSegment?.bookingInfos?.length - 1];
        
        const rowData = {
          id: i,
          travellerName: `${traveller?.firstName} ${traveller?.lastName}`,
          pnr: existingBookingInfo?.pnrNumber ?? selectedSegment?.pnrNumber,
          ticketNumber: existingBookingInfo?.ticketNumber ?? null,
          travellerId: traveller?.id,
          segmentId: segmentId,
          travellerKey: travellerKey
        };
        
        return rowData;
      }),
    [data, selectedSegment?.bookingInfos, segmentId]
  );
  
  const currentPNR = segmentPNRs[segmentKey] || 
  (selectedSegment?.bookingInfos?.[selectedSegment?.bookingInfos?.length - 1]?.pnrNumber ?? 
  selectedSegment?.pnrNumber);
  
  return (
    <Box>
      <Box display='flex' gap='6px' alignItems='center' justifyContent='space-between'>
        <Box
          display='flex'
          gap='6px'
          alignItems='center'
          padding='16px'
          justifyContent='space-between'>
          <Box>
            <img
              width={32}
              height={32}
              src={`https://oa-flight-assets.s3.ap-south-1.amazonaws.com/airline-logos/${selectedSegment?.airline?.code}.svg`}
              alt='Indigo'
              loading='lazy'
              style={{ borderRadius: '50%' }}
            />
          </Box>
          <Box>
            <Box display='flex' gap='6px' alignItems='center'>
              <OATypography variant='h2'>{selectedSegment?.departure?.airport?.city}</OATypography>
              <SourceDestIcon />
              <OATypography variant='h2'>{selectedSegment?.arrival?.airport?.city}</OATypography>
            </Box>
            <OATypography variant='body2' sx={{ color: theme.palette.grey[400] }}>
              {getFormattedDate(selectedSegment?.departure?.date, 'ddd, DD MMM, YYYY')}
            </OATypography>
          </Box>
        </Box>
        <Box>
          <label htmlFor='pnr' className='form-label required'>
            PNR
          </label>
          <input
            type='text'
            className='form-control form-control-sm form-control-solid oa-text-search w-150px ps-9'
            placeholder='Enter PNR'
            defaultValue={currentPNR ?? ''}
            onChange={e => {
              setSegmentPNRs((prev: any) => ({
                ...prev,
                [segmentKey]: e?.target?.value
              }));
            }}
          />
        </Box>
      </Box>
      <OATableComponent columns={tableColumns} data={tableData} hideFooter />
    </Box>
  );
};

export default TripType;
